import React from "react";
import { Card, EmptyState } from "../components/DesignSystem";
import type { PlannerModuleKey } from "../domain/types";
import { PREMIUM_MODULE_LABELS } from "../domain/planCatalog";
import { usePlannerData } from "../state/PlannerDataContext";
import { ModuleInterestAction } from "./ModuleInterestAction";

const MODULE_DESCRIPTIONS: Partial<Record<PlannerModuleKey, string>> = {
  rsvp: "Collect guest responses per household, track dietary needs, and see attendance roll up automatically.",
  invitations: "Track which household is invited to which celebration and send digital invitations.",
  guest_import: "Bulk-import your guest list from a spreadsheet instead of adding people one at a time.",
  saved_views: "Save a named filter and column selection on the guest list so you can return to it instantly.",
  seating: "Unlock seating plans, table capacities, confirmed-guest placement and stationery calculations.",
  payments: "Schedule and record payments against your budget, with paid/outstanding totals reconciled automatically.",
  collaborators: "Invite your partner, family, or planner to help manage this wedding together.",
  calendar: "See every celebration, task, and payment due date in one combined timeline.",
  runsheets: "Build a minute-by-minute run of show for each celebration.",
  dayof: "A single wedding-day view combining the run sheet, key contacts, transport, and outstanding tasks.",
  website: "Publish a guest-facing wedding website with your schedule, registry, and RSVP.",
  menu: "Plan courses per celebration and see dietary/meal counts derived from confirmed RSVPs.",
  travel: "Coordinate hotel room blocks, accommodation, and guest arrivals.",
  transport: "Plan shuttle routes and match seats to confirmed guests.",
  moodboards: "Collect inspiration images and define your wedding's style per celebration.",
  outfits: "Track outfits, fittings, and vendor details for each person and celebration.",
  beauty: "Schedule hair/makeup trials and appointments.",
  photography: "Manage photography coverage, shot lists, and photo groupings per celebration.",
  gifts: "Auto-calculate favour quantities from your guest and household counts.",
  gifts_received: "Privately track gifts received and thank-you status."
};

function useGuestContext() {
  const { workspace } = usePlannerData();
  const guestCount = workspace?.people.length ?? 0;
  const householdCount = workspace?.households.length ?? 0;
  return { guestCount, householdCount };
}

export function LockedModuleScreen({ moduleKey }: { moduleKey: PlannerModuleKey }) {
  const label = PREMIUM_MODULE_LABELS[moduleKey] || moduleKey;
  const description = MODULE_DESCRIPTIONS[moduleKey];
  const { guestCount, householdCount } = useGuestContext();
  const showGuestContext = ["seating", "invitations", "rsvp", "gifts", "gifts_received", "menu", "travel", "transport"].includes(moduleKey);

  return (
    <div className="pob-pad" style={{ maxWidth: "var(--prose-max)" }}>
      <Card padding="var(--space-6)">
        <EmptyState
          icon="lock"
          title={label}
          description={description}
          compact
          action={
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", alignItems: "flex-start" }}>
              {showGuestContext && guestCount > 0 && (
                <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>
                  You currently have {guestCount} guests across {householdCount} households.
                </p>
              )}
              <ModuleInterestAction moduleKey={moduleKey} moduleLabel={label} />
            </div>
          }
        />
      </Card>
    </div>
  );
}
